// Data + audio engine for the /keyboard lesson-plan practice page.
//
// The full "Keyboard for Playing With People" lesson plan is encoded here as
// structured data so the page can render it and track per-item progress with
// stable ids. Reference data (diatonic chord sets, recipes, starter songs) and
// a small Web Audio engine for the ear-training drills live here too — no audio
// library, just the native AudioContext (same lightweight posture as Timer.js).

/* ============================================================
   Audio engine (native Web Audio API)
   ============================================================ */

// Pitch-class → semitone offset from C. Exported so the trainer can compute
// chord-construction recipes (the half-step gaps that stack a triad).
export const SEMITONES = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5,
  "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
  // Enharmonic spellings that proper letter-per-degree scale spelling can
  // produce (e.g. E# in F# major, Cb in Eb minor) — valid names, same pitches.
  "E#": 5, "B#": 0, Cb: 11, Fb: 4,
};

let audioCtx;
let muted = false;

// Global mute, set from the Number Sense settings panel. Every voice checks it,
// so muting silences lessons, games, the piano, and count-in clicks alike.
export const setMuted = (m) => {
  muted = Boolean(m);
};

// Lazily create (and resume) a single AudioContext. Must be called from a user
// gesture the first time so browsers allow playback. Returns null if the
// browser has no Web Audio support.
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

// How many lead-in clicks a count-in plays (one 4/4 bar at the loop tempo).
export const COUNT_IN_BEATS = 4;

// A shared output bus: an envelope gain feeding a gentle lowpass, so the synth
// reads warmer and rounder than a bare oscillator into the destination.
const makeBus = (ctx, t0, dur, peak) => {
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2400;
  lp.Q.value = 0.7;
  lp.connect(ctx.destination);
  const master = ctx.createGain();
  master.connect(lp);
  master.gain.setValueAtTime(0.0001, t0);
  master.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  return master;
};

// One pitch, fattened: a triangle core plus two faintly detuned sine partials
// for a fuller, less buzzy tone than a single triangle wave.
const voiceFreq = (ctx, freq, bus, t0, dur) => {
  [
    ["triangle", 0, 1],
    ["sine", -7, 0.4],
    ["sine", 7, 0.4],
  ].forEach(([type, detune, gain]) => {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = detune;
    const vg = ctx.createGain();
    vg.gain.value = gain;
    osc.connect(vg);
    vg.connect(bus);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  });
};

// A short percussive click for the count-in metronome. `accent` marks beat one.
export const playClick = (whenSec = 0, accent = false) => {
  const ctx = getCtx();
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + whenSec;
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(accent ? 0.22 : 0.13, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.06);
  const osc = ctx.createOscillator();
  osc.type = "square";
  osc.frequency.value = accent ? 1600 : 1100;
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + 0.08);
};

// Whether this browser can synthesize tones at all (used to hide audio UI).
export const audioSupported = () =>
  typeof window !== "undefined" &&
  Boolean(window.AudioContext || window.webkitAudioContext);

// Turn a list of pitch-class names (e.g. ["C","E","G"]) into frequencies,
// stacking them into one ascending voicing starting around the middle of the
// keyboard (octave 4) so triads sound like a chord, not a spread.
const notesToFreqs = (notes) => {
  let octave = 4;
  let prev = -1;
  return notes.map((name) => {
    const s = SEMITONES[name];
    if (s <= prev) octave += 1; // keep the voicing ascending
    prev = s;
    const midi = (octave + 1) * 12 + s;
    return 440 * 2 ** ((midi - 69) / 12);
  });
};

// Play a chord (array of pitch-class names) for durationMs, optionally delayed
// by `whenSec` seconds from now. Uses a short attack/release envelope on a
// shared gain node to avoid clicks, with one oscillator per note.
export const playChord = (notes, durationMs = 900, whenSec = 0) => {
  const ctx = getCtx();
  if (!ctx || muted || !notes || !notes.length) return;
  const t0 = ctx.currentTime + whenSec;
  const dur = durationMs / 1000;
  const bus = makeBus(ctx, t0, dur, 0.12);
  notesToFreqs(notes).forEach((freq) => voiceFreq(ctx, freq, bus, t0, dur));
};

// Play a sequence of chords (each an array of pitch-class names), one per beat.
export const playProgression = (chords, beatMs = 900) => {
  if (!getCtx() || muted || !chords) return;
  chords.forEach((notes, i) => playChord(notes, beatMs * 0.95, (i * beatMs) / 1000));
};

// Convert a MIDI note number to its frequency in Hz (A4 = 69 = 440 Hz). This is
// the same formula inlined in notesToFreqs above, exported for the on-screen
// piano which needs to sound specific keys (not bare pitch classes).
export const midiToFreq = (midi) => 440 * 2 ** ((midi - 69) / 12);

// Play a single note at a specific MIDI pitch, so a tapped on-screen key sounds
// at its true octave (unlike playChord, which voices bare pitch-classes around
// C4). Same envelope/oscillator posture as playChord.
export const playNote = (midi, durationMs = 750, whenSec = 0) => {
  const ctx = getCtx();
  if (!ctx || muted || midi == null) return;
  const t0 = ctx.currentTime + whenSec;
  const dur = durationMs / 1000;
  const bus = makeBus(ctx, t0, dur, 0.16);
  voiceFreq(ctx, midiToFreq(midi), bus, t0, dur);
};

// On-screen piano layout: two octaves, C4–C6 (25 keys). White keys carry a
// running `wi` index (0…14) used for horizontal layout; each black key shares
// the `wi` of the white key it sits to the right of. Accidentals are spelled
// with sharps to match the sharp spelling used throughout DIATONIC, so a built
// chord can be validated by pitch-class set against notesFor().
export const PIANO_KEYS = (() => {
  const WHITE = ["C", "D", "E", "F", "G", "A", "B"];
  const HAS_SHARP = { C: true, D: true, E: false, F: true, G: true, A: true, B: false };
  const keys = [];
  let wi = 0;
  for (let oct = 4; oct <= 5; oct += 1) {
    for (let n = 0; n < WHITE.length; n += 1) {
      const name = WHITE[n];
      const midi = (oct + 1) * 12 + SEMITONES[name];
      keys.push({ name, octave: oct, midi, black: false, wi });
      if (HAS_SHARP[name]) {
        keys.push({ name: `${name}#`, octave: oct, midi: midi + 1, black: true, wi });
      }
      wi += 1;
    }
  }
  keys.push({ name: "C", octave: 6, midi: 7 * 12, black: false, wi });
  return keys;
})();

/* ============================================================
   Web MIDI helpers — turn raw MIDI into the same { name, octave }
   shape the on-screen Piano emits, so a real keyboard feeds the same
   `pressed` set. Pure (no browser API) so they're unit-testable.
   ============================================================ */

// Pitch-class names spelled with sharps, matching PIANO_KEYS / DIATONIC, so a
// note played on a MIDI keyboard validates by the same pitch-class set.
const MIDI_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// A MIDI note number → the Piano-style key it represents. MIDI 60 = C4.
export const midiToKey = (midi) => ({
  name: MIDI_NAMES[((midi % 12) + 12) % 12],
  octave: Math.floor(midi / 12) - 1,
  midi,
});

// Parse a raw MIDI message ([status, note, velocity]). Returns the note number
// and whether it's a note-on (velocity > 0) or note-off. A note-on with zero
// velocity is the common "running status" way to signal note-off. Returns null
// for anything that isn't a note event (control change, clock, etc).
export const parseMidiMessage = (data) => {
  if (!data || data.length < 2) return null;
  const command = data[0] & 0xf0;
  const midi = data[1];
  const velocity = data.length > 2 ? data[2] : 0;
  if (command === 0x90 && velocity > 0) return { type: "on", midi, velocity };
  if (command === 0x80 || (command === 0x90 && velocity === 0))
    return { type: "off", midi, velocity: 0 };
  return null;
};

// Does this browser expose the Web MIDI API? (Safari historically does not.)
export const midiSupported = () =>
  typeof navigator !== "undefined" && typeof navigator.requestMIDIAccess === "function";

/* ---- MIDI range calibration ----
   Small boards (25 keys) and octave-shifted keyboards send notes outside the
   on-screen piano's C4–C6 window, so presses sound but never light up — the
   screen "can't see" most of the keyboard. The settings panel captures the
   player's physical range (hit the lowest key, hit the highest) and every
   incoming note is shifted by whole octaves so that range best overlaps the
   screen window. Octave shifts preserve pitch class, so the graders (which
   compare pitch-class sets, or the lowest-note ordering for inversions) are
   untouched — the physical keyboard just becomes the visible one. */

export const PIANO_LOW = PIANO_KEYS[0].midi; // C4 = 60
export const PIANO_HIGH = PIANO_KEYS[PIANO_KEYS.length - 1].midi; // C6 = 84

// The whole-octave shift that best overlaps a physical [low, high] range with
// the screen window. Ties go to the smaller shift, so a board that already
// covers the window (61/88 keys) stays put. Pure, so it's unit-testable.
export const midiRangeShift = (low, high) => {
  if (!Number.isFinite(low) || !Number.isFinite(high) || low > high) return 0;
  let best = 0;
  let bestOverlap = -Infinity;
  for (let shift = -120; shift <= 120; shift += 12) {
    const overlap =
      Math.min(high + shift, PIANO_HIGH) - Math.max(low + shift, PIANO_LOW);
    if (
      overlap > bestOverlap ||
      (overlap === bestOverlap && Math.abs(shift) < Math.abs(best))
    ) {
      bestOverlap = overlap;
      best = shift;
    }
  }
  return best;
};

// The active shift, set from the persisted settings (same posture as the
// global mute): the page pushes the calibrated range in, every MIDI hook maps
// incoming notes through mapMidiInput. Nulls clear the calibration.
let midiShift = 0;
export const setMidiRange = (low, high) => {
  midiShift = low == null || high == null ? 0 : midiRangeShift(low, high);
};

// The note a raw incoming MIDI note plays as, under the current calibration.
export const mapMidiInput = (midi) => midi + midiShift;

/* ============================================================
   Reference data — diatonic chord sets (Appendix A)
   ============================================================ */

// Degree order is fixed: I ii iii IV V vi vii°. `notes` are pitch classes.
export const DIATONIC = {
  C: [
    { deg: "I", chord: "C", notes: ["C", "E", "G"] },
    { deg: "ii", chord: "Dm", notes: ["D", "F", "A"] },
    { deg: "iii", chord: "Em", notes: ["E", "G", "B"] },
    { deg: "IV", chord: "F", notes: ["F", "A", "C"] },
    { deg: "V", chord: "G", notes: ["G", "B", "D"] },
    { deg: "vi", chord: "Am", notes: ["A", "C", "E"] },
    { deg: "vii°", chord: "B°", notes: ["B", "D", "F"] },
  ],
  G: [
    { deg: "I", chord: "G", notes: ["G", "B", "D"] },
    { deg: "ii", chord: "Am", notes: ["A", "C", "E"] },
    { deg: "iii", chord: "Bm", notes: ["B", "D", "F#"] },
    { deg: "IV", chord: "C", notes: ["C", "E", "G"] },
    { deg: "V", chord: "D", notes: ["D", "F#", "A"] },
    { deg: "vi", chord: "Em", notes: ["E", "G", "B"] },
    { deg: "vii°", chord: "F#°", notes: ["F#", "A", "C"] },
  ],
  D: [
    { deg: "I", chord: "D", notes: ["D", "F#", "A"] },
    { deg: "ii", chord: "Em", notes: ["E", "G", "B"] },
    { deg: "iii", chord: "F#m", notes: ["F#", "A", "C#"] },
    { deg: "IV", chord: "G", notes: ["G", "B", "D"] },
    { deg: "V", chord: "A", notes: ["A", "C#", "E"] },
    { deg: "vi", chord: "Bm", notes: ["B", "D", "F#"] },
    { deg: "vii°", chord: "C#°", notes: ["C#", "E", "G"] },
  ],
  // A is not shown in the reference cards (Appendix A lists C/G/D) but is here
  // so "Stand By Me" (key of A) can play its changes.
  A: [
    { deg: "I", chord: "A", notes: ["A", "C#", "E"] },
    { deg: "ii", chord: "Bm", notes: ["B", "D", "F#"] },
    { deg: "iii", chord: "C#m", notes: ["C#", "E", "G#"] },
    { deg: "IV", chord: "D", notes: ["D", "F#", "A"] },
    { deg: "V", chord: "E", notes: ["E", "G#", "B"] },
    { deg: "vi", chord: "F#m", notes: ["F#", "A", "C#"] },
    { deg: "vii°", chord: "G#°", notes: ["G#", "B", "D"] },
  ],
};

// Keys whose full diatonic table is rendered in the reference section.
export const REF_KEYS = ["C", "G", "D"];

/* ============================================================
   Diatonic seventh chords — one more stacked third on each triad.
   Same shape as DIATONIC, derived from each key's major scale so the
   four-note voicings always agree with the triads above. Degree tokens
   carry the chord quality (Imaj7, ii7, V7, vii°7 = half-diminished) so
   they read as proper roman numerals, not a misleading blanket "7".
   ============================================================ */
const MAJOR_SCALES = {
  C: ["C", "D", "E", "F", "G", "A", "B"],
  G: ["G", "A", "B", "C", "D", "E", "F#"],
  D: ["D", "E", "F#", "G", "A", "B", "C#"],
  A: ["A", "B", "C#", "D", "E", "F#", "G#"],
};
const SEVENTH_DEGS = ["Imaj7", "ii7", "iii7", "IVmaj7", "V7", "vi7", "vii°7"];
const SEVENTH_SUFFIX = ["maj7", "m7", "m7", "maj7", "7", "m7", "m7♭5"];

export const DIATONIC7 = Object.fromEntries(
  Object.entries(MAJOR_SCALES).map(([key, scale]) => [
    key,
    scale.map((root, i) => ({
      deg: SEVENTH_DEGS[i],
      chord: root + SEVENTH_SUFFIX[i],
      // root, 3rd, 5th, 7th — diatonic thirds stacked within the scale
      notes: [scale[i], scale[(i + 2) % 7], scale[(i + 4) % 7], scale[(i + 6) % 7]],
    })),
  ])
);

/* ============================================================
   Diatonic triads of the natural minor (Aeolian) keys.
   Same shape as DIATONIC, derived from each minor key's scale so the
   voicings agree with the relative major above. Degree tokens use
   minor-key roman numerals (i, ii°, III, iv, v, VI, VII). Keys are named
   with a trailing "m" (Am, Em…) so they never collide with the major
   tables keyed by bare letters.
   ============================================================ */
const MINOR_SCALES = {
  Am: ["A", "B", "C", "D", "E", "F", "G"],
  Em: ["E", "F#", "G", "A", "B", "C", "D"],
  Bm: ["B", "C#", "D", "E", "F#", "G", "A"],
  "F#m": ["F#", "G#", "A", "B", "C#", "D", "E"],
};
const MINOR_DEGS = ["i", "ii°", "III", "iv", "v", "VI", "VII"];
const MINOR_SUFFIX = ["m", "°", "", "m", "m", "", ""];

export const DIATONIC_MINOR = Object.fromEntries(
  Object.entries(MINOR_SCALES).map(([key, scale]) => [
    key,
    scale.map((root, i) => ({
      deg: MINOR_DEGS[i],
      chord: root + MINOR_SUFFIX[i],
      notes: [scale[i], scale[(i + 2) % 7], scale[(i + 4) % 7]],
    })),
  ])
);

// The seven scale notes of a key (major or minor), or null if unknown.
export const scaleFor = (key) => MAJOR_SCALES[key] || MINOR_SCALES[key] || null;

// The scale as an ascending MIDI run — root in octave 4 up to the octave above
// (8 notes), bumping octaves as pitch-classes wrap so it always climbs.
export const scaleMidis = (key) => {
  const scale = scaleFor(key);
  if (!scale) return [];
  const out = [];
  let octave = 4;
  let prev = -1;
  scale.forEach((name) => {
    const s = SEMITONES[name];
    if (s <= prev) octave += 1;
    prev = s;
    out.push((octave + 1) * 12 + s);
  });
  out.push(out[0] + 12); // resolve up to the octave
  return out;
};

// Play a key's scale ascending, one note per beat.
export const playScale = (key, beatMs = 300) => {
  if (!getCtx() || muted) return;
  scaleMidis(key).forEach((m, i) => playNote(m, beatMs * 0.9, (i * beatMs) / 1000));
};

// Look up a degree's notes in a key (e.g. notesFor("C","V") -> ["G","B","D"]).
// Resolves across the major triad, seventh, and minor tables by key + degree.
export const notesFor = (key, deg) => {
  for (const table of [DIATONIC, DIATONIC7, DIATONIC_MINOR]) {
    const found = (table[key] || []).find((c) => c.deg === deg);
    if (found) return found.notes;
  }
  return null;
};

// Map a list of roman-numeral degrees to their chord notes in a key.
export const chordsForProgression = (key, degrees) =>
  degrees.map((deg) => notesFor(key, deg)).filter(Boolean);

// Shell / rootless voicing — the chord's *guide tones*, with the root dropped so
// the bassist owns the root and the section stays clean. For a 7th chord
// (notesFor length 4) that's the 3rd + 7th; for a triad (length 3) it's the
// 3rd + 5th (a "rootless triad"). Returns [] when the degree is unknown.
export const shellVoicing = (key, deg) => {
  const notes = notesFor(key, deg);
  if (!notes || !notes.length) return [];
  if (notes.length >= 4) return [notes[1], notes[3]]; // 3rd, 7th
  if (notes.length === 3) return [notes[1], notes[2]]; // 3rd, 5th
  return [];
};

/* ============================================================
   Rhythm section — a looping groove to comp over (the "jam room").
   Synth drums (kick / snare / hat) + a bass root on the changes, with
   optional chord comping in a chosen rhythmic pattern. No samples: every
   drum is shaped from oscillators / noise, like the rest of the engine.
   ============================================================ */

// A reusable white-noise buffer (built lazily, once) for snare / hat.
let noiseBuf = null;
const getNoise = (ctx) => {
  if (noiseBuf) return noiseBuf;
  const buf = ctx.createBuffer(1, ctx.sampleRate * 0.4, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuf = buf;
  return buf;
};

const playKick = (whenSec = 0) => {
  const ctx = getCtx();
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + whenSec;
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.setValueAtTime(0.9, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, t0);
  osc.frequency.exponentialRampToValueAtTime(48, t0 + 0.12);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + 0.18);
};

const playSnare = (whenSec = 0) => {
  const ctx = getCtx();
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + whenSec;
  // noise body
  const noise = ctx.createBufferSource();
  noise.buffer = getNoise(ctx);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 1500;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.5, t0);
  ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
  noise.connect(hp);
  hp.connect(ng);
  ng.connect(ctx.destination);
  noise.start(t0);
  noise.stop(t0 + 0.16);
  // a little tonal "crack"
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.value = 180;
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.25, t0);
  og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
  osc.connect(og);
  og.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + 0.1);
};

const playHat = (whenSec = 0, open = false) => {
  const ctx = getCtx();
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + whenSec;
  const noise = ctx.createBufferSource();
  noise.buffer = getNoise(ctx);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 7000;
  const g = ctx.createGain();
  const dur = open ? 0.18 : 0.045;
  g.gain.setValueAtTime(0.18, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  noise.connect(hp);
  hp.connect(g);
  g.connect(ctx.destination);
  noise.start(t0);
  noise.stop(t0 + dur + 0.02);
};

// Comping rhythms, expressed as the 16th-note positions in a bar (0..15) where
// the chord is struck. `arp` walks one chord tone per hit instead of the block.
export const COMP_PATTERNS = {
  pads: { label: "pads", hits: [0], sustain: 15 },
  stabs: { label: "stabs", hits: [6, 14], sustain: 2 },
  eighths: { label: "8ths", hits: [0, 2, 4, 6, 8, 10, 12, 14], sustain: 1.6 },
  arp: { label: "arp", hits: [0, 4, 8, 12], sustain: 3, arp: true },
  push: { label: "push", hits: [0, 6], sustain: 5 }, // the Charleston: downbeat + the pushed "and of 2"
  half: { label: "half-time", hits: [0, 8], sustain: 7 }, // two long pads a bar — ballad support
  broken: { label: "broken", hits: [0, 2, 8, 10], sustain: 1.8, arp: true }, // gentle broken-chord figure
};

const DRUM_KICK = [0, 8, 10];
const DRUM_SNARE = [4, 12];
const DRUM_HAT = [0, 2, 4, 6, 8, 10, 12, 14];

// A rest bar in a progression — nobody comps, the drummer fills. Lives here
// (not in the curriculum layer) because the groove has to understand it too.
export const REST = "rest";

// The fill the drummer plays through a rest bar: the kick anchors the downbeat,
// then the snare rolls into the next bar — the cue a band player lays out for.
const FILL_SNARE = [4, 8, 10, 12, 14];

// The pitch-class root of the chord at a bar, as a bass MIDI note (octave 2).
const bassMidiFor = (key, deg) => {
  const notes = notesFor(key, deg);
  if (!notes || !notes.length) return null;
  return 36 + SEMITONES[notes[0]]; // C2 = 36
};

// Pure description of what sounds on a given 16th-note `step` of the loop: which
// drums fire, the bass note, and (when comping) the chord notes for the pattern.
// Pure (no audio) so the groove logic is unit-testable.
export const grooveStepEvents = (
  step,
  key,
  degrees,
  patternKey = "pads",
  comp = false,
  rootless = false
) => {
  const pos = ((step % 16) + 16) % 16;
  const barIdx = degrees.length
    ? ((Math.floor(step / 16) % degrees.length) + degrees.length) % degrees.length
    : 0;
  const deg = degrees[barIdx];
  const rest = deg === REST;
  // When rootless, the comped chord is the shell (guide tones) — the bass still
  // sounds the root below, so it reads like a real section: bass owns the root,
  // keys play the guide tones.
  const compNotes = rootless ? shellVoicing(key, deg) : notesFor(key, deg) || [];
  const pat = COMP_PATTERNS[patternKey] || COMP_PATTERNS.pads;
  const ev = {
    pos,
    barIdx,
    deg,
    rest,
    // a rest bar is the drummer's: kick holds the downbeat, the snare fills
    kick: rest ? pos === 0 : DRUM_KICK.includes(pos),
    snare: rest ? FILL_SNARE.includes(pos) : DRUM_SNARE.includes(pos),
    hat: DRUM_HAT.includes(pos),
    bass: pos === 0 || pos === 8 ? bassMidiFor(key, deg) : null,
    chord: null,
  };
  if (comp && compNotes.length && pat.hits.includes(pos)) {
    // an arp pattern walks the chord one tone per hit, in hit order — indexed
    // by the hit's position *in the pattern* so off-grid hits (pos 2, 6, 10…)
    // still land on a real chord tone
    ev.chord = pat.arp
      ? [compNotes[pat.hits.indexOf(pos) % compNotes.length]]
      : compNotes;
  }
  return ev;
};

// Start a looping groove. Returns { stop, startTime, barDur, bars } so the UI
// can highlight the current bar against the audio clock. Schedules ~200ms ahead
// on a 25ms timer (the standard Web Audio look-ahead pattern).
export const startGroove = ({
  key,
  degrees = [],
  bpm = 100,
  pattern = "pads",
  comp = false,
  rootless = false,
} = {}) => {
  const ctx = getCtx();
  const sixteenth = 60 / bpm / 4;
  const barDur = sixteenth * 16;
  if (!ctx || !degrees.length) {
    return { stop() {}, startTime: 0, barDur, bars: degrees.length };
  }
  const startTime = ctx.currentTime + 0.15;
  let step = 0;
  let nextTime = startTime;
  const schedule = () => {
    while (nextTime < ctx.currentTime + 0.2) {
      const ev = grooveStepEvents(step, key, degrees, pattern, comp, rootless);
      const when = nextTime - ctx.currentTime;
      if (ev.kick) playKick(when);
      if (ev.snare) playSnare(when);
      if (ev.hat) playHat(when, ev.pos % 4 === 2);
      if (ev.bass != null) playNote(ev.bass, 240, when);
      if (ev.chord) {
        const pat = COMP_PATTERNS[pattern] || COMP_PATTERNS.pads;
        playChord(ev.chord, sixteenth * pat.sustain * 1000, when);
      }
      nextTime += sixteenth;
      step += 1;
    }
  };
  schedule();
  const timer = setInterval(schedule, 25);
  return {
    stop() {
      clearInterval(timer);
    },
    startTime,
    barDur,
    bars: degrees.length,
  };
};

/* ---- chart transport (#87): play a chart's bars once through ----
   A chart performance isn't a loop: each bar carries its own { key, deg } —
   mid-chart key changes are the point — and the run ends after the last bar.
   The rhythm section plays drums + bass only; the comping is the player's job.
   Rest bars get the drummer's fill exactly like the groove (grooveStepEvents
   already understands REST). */

// Pure per-step description of a chart performance: the bar's own (key, deg)
// treated as a one-bar loop, or null once the chart is over.
export const chartStepEvents = (step, bars = []) => {
  const barIdx = Math.floor(step / 16);
  if (step < 0 || barIdx >= bars.length) return null;
  const bar = bars[barIdx];
  return { ...grooveStepEvents(step % 16, bar.key, [bar.deg]), barIdx, key: bar.key };
};

// Start a one-pass chart performance. Same handle shape as startGroove
// ({ stop, startTime, barDur, bars }) so the screen's transport code carries over.
export const startChart = ({ bars = [], bpm = 100 } = {}) => {
  const ctx = getCtx();
  const sixteenth = 60 / bpm / 4;
  const barDur = sixteenth * 16;
  if (!ctx || !bars.length) {
    return { stop() {}, startTime: 0, barDur, bars: bars.length };
  }
  const startTime = ctx.currentTime + 0.15;
  const total = bars.length * 16;
  let step = 0;
  let nextTime = startTime;
  const schedule = () => {
    while (nextTime < ctx.currentTime + 0.2 && step < total) {
      const ev = chartStepEvents(step, bars);
      const when = nextTime - ctx.currentTime;
      if (ev) {
        if (ev.kick) playKick(when);
        if (ev.snare) playSnare(when);
        if (ev.hat) playHat(when, ev.pos % 4 === 2);
        if (ev.bass != null) playNote(ev.bass, 240, when);
      }
      nextTime += sixteenth;
      step += 1;
    }
  };
  schedule();
  const timer = setInterval(schedule, 25);
  return {
    stop() {
      clearInterval(timer);
    },
    startTime,
    barDur,
    bars: bars.length,
  };
};

// The audio clock's current time (for syncing UI highlights to playback).
export const audioNow = () => {
  const ctx = getCtx();
  return ctx ? ctx.currentTime : 0;
};

/* ============================================================
   Rhythm grading — for the pattern trainer. The player copies a comping
   pattern; we capture chord strikes (audio-clock timestamps), then score
   how well they land on the pattern's 16th-note grid. Pure + testable;
   forgiving by design (we judge the rhythmic *pattern*, not micro-timing).
   ============================================================ */

// Collapse a list of near-simultaneous onsets into one strike each — a played
// chord arrives as several note-ons within a few ms, but it's one rhythmic hit.
export const groupStrikes = (times, windowSec = 0.07) => {
  const sorted = [...times].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((t) => {
    if (!out.length || t - out[out.length - 1] > windowSec) out.push(t);
  });
  return out;
};

// Score grouped strikes against an expected pattern (16th positions 0..15) over
// `bars` bars from `start` (audio-clock seconds, the measurement window's first
// downbeat) at `sixteenth` seconds per 16th. Each strike quantizes to the
// nearest 16th — half a 16th of slop either way — so being a hair off still
// counts. Returns a 0–100 match score plus the detail the UI highlights.
export const scoreRhythm = (strikes, start, sixteenth, expected, bars) => {
  const total = expected.length * bars;
  const expectedAbs = new Set();
  for (let b = 0; b < bars; b += 1) expected.forEach((p) => expectedAbs.add(b * 16 + p));
  const struckAbs = new Set();
  strikes.forEach((t) => {
    const i = Math.round((t - start) / sixteenth);
    if (i >= 0 && i < bars * 16) struckAbs.add(i);
  });
  let matched = 0;
  expectedAbs.forEach((i) => {
    if (struckAbs.has(i)) matched += 1;
  });
  let extra = 0;
  struckAbs.forEach((i) => {
    if (!expectedAbs.has(i)) extra += 1;
  });
  const raw = total ? matched / total : 0;
  const penalty = total ? Math.min(0.5, (extra / total) * 0.5) : 0;
  const score = Math.max(0, Math.round((raw - penalty) * 100));
  const struckPos = [...new Set([...struckAbs].map((i) => i % 16))];
  return { score, matched, total, extra, struckPos };
};

export const RECIPES = [
  { title: "Major triad", text: "root, +4 half-steps, +3 half-steps." },
  {
    title: "Minor triad",
    text: "same as major, middle note down one half-step (= root, +3, +4).",
  },
  { title: "Major scale step-pattern", text: "W–W–H–W–W–W–H." },
  {
    title: "Chord quality by degree (any key)",
    text: "I maj · ii min · iii min · IV maj · V maj · vi min · vii° dim.",
  },
];

/* ============================================================
   Starter songs (Appendix B)
   ============================================================ */

// spotifyId is null where Spotify search is licensor-restricted; the page falls
// back to a "Search on Spotify" link. `numbers` drive the "play the changes"
// button via chordsForProgression(key, numbers). Loops are the song's core
// vamp as commonly charted (a few entries note "as commonly played" where the
// campfire key differs from the record); minor-key entries use the natural-
// minor numerals (i, VI, VII…) and unlock with the minor track's keys.
export const STARTER_SONGS = [
  // ---- the original five ----
  // These carry a playable `form` (ordered sections, each a loop × repeats) so
  // chart mode can run a whole song shape, not just its signature 4 bars. The
  // forms are honest simplifications of the records, all in the song's key.
  {
    title: "With or Without You", artist: "U2", key: "D",
    chords: "D–A–Bm–G", numbers: ["I", "V", "vi", "IV"],
    teaches: "The canonical loop, never breaks", spotifyId: null,
    form: [
      { name: "verse", loop: ["I", "V", "vi", "IV"], repeats: 2 },
      { name: "chorus", loop: ["I", "V", "vi", "IV"], repeats: 2 }, // it really never breaks
    ],
  },
  {
    title: "Let It Be", artist: "The Beatles", key: "C",
    chords: "C–G–Am–F", numbers: ["I", "V", "vi", "IV"],
    teaches: "Same numbers, different key", spotifyId: null,
    form: [
      { name: "verse", loop: ["I", "V", "vi", "IV"], repeats: 2 },
      { name: "chorus", loop: ["vi", "V", "IV", "I"], repeats: 2 }, // "let it be, let it be…"
    ],
  },
  {
    title: "Stand By Me", artist: "Ben E. King", key: "A",
    chords: "A–F#m–D–E", numbers: ["I", "vi", "IV", "V"],
    teaches: "Order matters (50s progression)", spotifyId: "3SdTKo2uVsxFblQjpScoHy",
    form: [
      // the real 8-bar 50s shape: home for two, vi for two, IV–V, home again
      { name: "verse", loop: ["I", "I", "vi", "vi", "IV", "V", "I", "I"], repeats: 2 },
    ],
  },
  {
    title: "Knockin' on Heaven's Door", artist: "Bob Dylan", key: "G",
    chords: "G–D–Am–C", numbers: ["I", "V", "ii", "IV"],
    teaches: "Slow changes, the ii chord", spotifyId: "6HSXNV0b4M4cLJ7ljgVVeh",
    form: [
      // the two alternating lines: I–V–ii, then I–V–IV
      { name: "verse", loop: ["I", "V", "ii", "ii", "I", "V", "IV", "IV"], repeats: 2 },
    ],
  },
  {
    title: "Riptide", artist: "Vance Joy", key: "C",
    chords: "Am–G–C–F", numbers: ["vi", "V", "I", "IV"],
    teaches: "Starts on the vi, not home", spotifyId: "7yq4Qj7cqayVTp3FF9CWbm",
    form: [
      { name: "verse", loop: ["vi", "V", "I", "I"], repeats: 2 },
      { name: "chorus", loop: ["vi", "V", "I", "IV"], repeats: 2 },
    ],
  },
  // ---- the four-chord-pop club (I–V–vi–IV) ----
  {
    title: "No Woman, No Cry", artist: "Bob Marley & The Wailers", key: "C",
    chords: "C–G–Am–F", numbers: ["I", "V", "vi", "IV"],
    teaches: "Identical numbers to Let It Be — hear the groove, not the loop", spotifyId: null,
  },
  {
    title: "Someone Like You", artist: "Adele", key: "A",
    chords: "A–E–F#m–D", numbers: ["I", "V", "vi", "IV"],
    teaches: "The loop at ballad tempo", spotifyId: null,
  },
  {
    title: "Demons", artist: "Imagine Dragons", key: "D",
    chords: "D–A–Bm–G", numbers: ["I", "V", "vi", "IV"],
    teaches: "Same numbers as U2, decades later", spotifyId: null,
  },
  {
    title: "Wagon Wheel", artist: "Old Crow Medicine Show", key: "G",
    chords: "G–D–Em–C", numbers: ["I", "V", "vi", "IV"],
    teaches: "The loop wearing a fiddle", spotifyId: null,
  },
  // ---- the 50s / doo-wop club (I–vi–IV–V) ----
  {
    title: "Duke of Earl", artist: "Gene Chandler", key: "C",
    chords: "C–Am–F–G", numbers: ["I", "vi", "IV", "V"],
    teaches: "The doo-wop turn, canonical", spotifyId: null,
  },
  {
    title: "Unchained Melody", artist: "The Righteous Brothers", key: "C",
    chords: "C–Am–F–G", numbers: ["I", "vi", "IV", "V"],
    teaches: "Same turn as Duke of Earl, slowed to a swoon", spotifyId: null,
  },
  {
    title: "Monster Mash", artist: "Bobby 'Boris' Pickett", key: "G",
    chords: "G–Em–C–D", numbers: ["I", "vi", "IV", "V"],
    teaches: "Novelty hit, same doo-wop bones", spotifyId: null,
  },
  {
    title: "Every Breath You Take", artist: "The Police", key: "G",
    chords: "G–Em–C–D", numbers: ["I", "vi", "IV", "V"],
    teaches: "50s changes at a creep (as commonly played)", spotifyId: null,
  },
  {
    title: "Please Mr. Postman", artist: "The Marvelettes", key: "D",
    chords: "D–Bm–G–A", numbers: ["I", "vi", "IV", "V"],
    teaches: "Motown runs on the same four", spotifyId: null,
  },
  {
    title: "I Will Always Love You", artist: "Dolly Parton", key: "A",
    chords: "A–F#m–D–E", numbers: ["I", "vi", "IV", "V"],
    teaches: "Identical numbers to Stand By Me", spotifyId: null,
  },
  {
    title: "Take Me Home, Country Roads", artist: "John Denver", key: "A",
    chords: "A–F#m–E–D", numbers: ["I", "vi", "V", "IV"],
    teaches: "The 50s changes, reshuffled — order is the song", spotifyId: null,
  },
  // ---- three-chord workhorses (I, IV, V) ----
  {
    title: "La Bamba", artist: "Ritchie Valens", key: "C",
    chords: "C–F–G–C", numbers: ["I", "IV", "V", "I"],
    teaches: "The workhorse at full sprint", spotifyId: null,
  },
  {
    title: "Twist and Shout", artist: "The Beatles", key: "D",
    chords: "D–G–A–A", numbers: ["I", "IV", "V", "V"],
    teaches: "Same three chords as La Bamba, on a rave-up", spotifyId: null,
  },
  {
    title: "Blowin' in the Wind", artist: "Bob Dylan", key: "D",
    chords: "D–G–A–D", numbers: ["I", "IV", "V", "I"],
    teaches: "Three chords carrying a whole song", spotifyId: null,
  },
  {
    title: "Ring of Fire", artist: "Johnny Cash", key: "G",
    chords: "G–C–G–D", numbers: ["I", "IV", "I", "V"],
    teaches: "Home keeps coming back between changes", spotifyId: null,
  },
  {
    title: "Brown Eyed Girl", artist: "Van Morrison", key: "G",
    chords: "G–C–G–D", numbers: ["I", "IV", "I", "V"],
    teaches: "Same numbers as Ring of Fire — the riff is the identity", spotifyId: null,
  },
  {
    title: "Good Riddance (Time of Your Life)", artist: "Green Day", key: "G",
    chords: "G–C–D–G", numbers: ["I", "IV", "V", "I"],
    teaches: "The workhorse strummed gently", spotifyId: null,
  },
  {
    title: "Lean on Me", artist: "Bill Withers", key: "C",
    chords: "C–F–C–G", numbers: ["I", "IV", "I", "V"],
    teaches: "Walk up to IV, lean back to V", spotifyId: null,
  },
  {
    title: "Three Little Birds", artist: "Bob Marley & The Wailers", key: "A",
    chords: "A–D–A–E", numbers: ["I", "IV", "I", "V"],
    teaches: "Three chords, zero worry", spotifyId: null,
  },
  {
    title: "Stir It Up", artist: "Bob Marley & The Wailers", key: "A",
    chords: "A–D–E–D", numbers: ["I", "IV", "V", "IV"],
    teaches: "A one-bar vamp that never resolves", spotifyId: null,
  },
  {
    title: "Wild Thing", artist: "The Troggs", key: "A",
    chords: "A–D–E–D", numbers: ["I", "IV", "V", "IV"],
    teaches: "Garage rock is Stir It Up with distortion", spotifyId: null,
  },
  {
    title: "Bad Moon Rising", artist: "Creedence Clearwater Revival", key: "D",
    chords: "D–A–G–D", numbers: ["I", "V", "IV", "I"],
    teaches: "V and IV swap places — hear the difference", spotifyId: null,
  },
  {
    title: "Thinking Out Loud", artist: "Ed Sheeran", key: "D",
    chords: "D–D–G–A", numbers: ["I", "I", "IV", "V"],
    teaches: "Two bars of home before the turn", spotifyId: null,
  },
  // ---- axis loops & friends (starting away from home) ----
  {
    title: "Save Tonight", artist: "Eagle-Eye Cherry", key: "C",
    chords: "Am–F–C–G", numbers: ["vi", "IV", "I", "V"],
    teaches: "The axis loop — four chords, starts dark", spotifyId: null,
  },
  {
    title: "Despacito", artist: "Luis Fonsi", key: "D",
    chords: "Bm–G–D–A", numbers: ["vi", "IV", "I", "V"],
    teaches: "Same axis loop, different continent", spotifyId: null,
  },
  {
    title: "Stay With Me", artist: "Sam Smith", key: "C",
    chords: "Am–F–C–C", numbers: ["vi", "IV", "I", "I"],
    teaches: "Three chords, round and round", spotifyId: null,
  },
  {
    title: "Viva la Vida", artist: "Coldplay", key: "G",
    chords: "C–D–G–Em", numbers: ["IV", "V", "I", "vi"],
    teaches: "Never sits on home (as commonly played)", spotifyId: null,
  },
  {
    title: "Hallelujah", artist: "Leonard Cohen", key: "C",
    chords: "F–G–Am–F", numbers: ["IV", "V", "vi", "IV"],
    teaches: "The lyric narrates its own changes — 'the fourth, the fifth…'", spotifyId: null,
  },
  {
    title: "What's Up?", artist: "4 Non Blondes", key: "A",
    chords: "A–Bm–D–A", numbers: ["I", "ii", "IV", "I"],
    teaches: "The ii chord in the wild (like Knockin')", spotifyId: null,
  },
  // ---- minor-key loops (natural-minor numbers) ----
  {
    title: "Zombie", artist: "The Cranberries", key: "Em",
    chords: "Em–C–G–D", numbers: ["i", "VI", "III", "VII"],
    teaches: "The signature minor loop, wall of guitar", spotifyId: null,
  },
  {
    title: "Ain't No Sunshine", artist: "Bill Withers", key: "Am",
    chords: "Am–Em–G–Am", numbers: ["i", "v", "VII", "i"],
    teaches: "Minor home, brooding v", spotifyId: null,
  },
  {
    title: "Jolene", artist: "Dolly Parton", key: "Am",
    chords: "Am–C–G–Am", numbers: ["i", "III", "VII", "i"],
    teaches: "Minor i to relative III (as played, capo'd Am shapes)", spotifyId: null,
  },
  {
    title: "All Along the Watchtower", artist: "Bob Dylan", key: "Am",
    chords: "Am–G–F–G", numbers: ["i", "VII", "VI", "VII"],
    teaches: "The minor descent, endlessly jammable (as commonly played)", spotifyId: null,
  },
];

export const GLOSSARY = [
  { term: "Tonic / I / “home”", def: "the chord the key is built on; everything resolves toward it." },
  { term: "Diatonic", def: "the chords/notes that belong to a given key." },
  { term: "Triad", def: "a three-note chord (root, third, fifth)." },
  { term: "Comping", def: "accompaniment — supporting the song rather than carrying it." },
  { term: "Voicing", def: "which notes of a chord you play and in what spread/register." },
  { term: "Cadence", def: "a chord move that signals resolution; V→I is the strongest." },
  { term: "Chord tones", def: "the notes of the chord currently sounding — the “strong” melody notes over it." },
  { term: "Roman numerals / Nashville numbers", def: "chords named by scale degree so they’re portable across keys." },
  { term: "Lead sheet / chord chart", def: "compressed song notation (melody + chord symbols, or just chords) — the shared protocol bands play from." },
];

/* ============================================================
   The phase map (overview table)
   ============================================================ */

export const PHASE_MAP = [
  { phase: "0", units: "Orientation", installs: "The model, how to practice" },
  { phase: "1", units: "First contact", installs: "One song, one key, playing along — day one" },
  { phase: "2", units: "Key as a place", installs: "Making C feel like home (the gravity)" },
  { phase: "3", units: "Portability", installs: "Numbers + transposing to G, D" },
  { phase: "4", units: "Comping craft", installs: "The actual band-support skill" },
  { phase: "5", units: "Ear (parallel)", installs: "Hearing the key and the changes — runs alongside from early" },
  { phase: "6", units: "Playing with a human", installs: "The shared protocol, taking direction" },
  { phase: "7", units: "Melody / improv", installs: "Optional, advanced — soloing over changes" },
];

/* ============================================================
   The plan itself — phases -> units
   Each unit: { id, title, goal?, concept?, drills?: [], doneWhen? }
   Checkbox ids derive as `${unit.id}-drill-${i}` and `${unit.id}-done`.
   ============================================================ */

export const PHASES = [
  {
    id: "0",
    title: "Phase 0 — Orientation",
    installs: "The model, how to practice",
    units: [
      {
        id: "0.1",
        title: "Unit 0.1 — The two instruments hiding in “keyboard”",
        goal: "Know which track you're on and why.",
        concept:
          "“Keyboard” splits into two paths. The notation track (classical, Alfred) optimizes for reproducing fixed written pieces solo — real theory, but it doesn't train real-time accompaniment. The functional track (rock/pop/folk/indie/gospel/blues all share this DNA) optimizes for “given these chords, what do I play right now.” Bands run on a shared protocol — chord charts, lead sheets, the Nashville Number System — basically an API for a song that lets strangers interoperate. Learning to operate on that representation is the skill.",
        doneWhen:
          "You can say in one sentence why you're learning chords-by-function instead of reading full sheet music.",
      },
      {
        id: "0.2",
        title: "Unit 0.2 — How to practice this",
        goal: "Adopt the practice loop that beats the infrastructure trap.",
        concept:
          "Isolation builds a chord; a song keeps it. Memorization without retrieval decays; memorization attached to a song you like encodes with a context handle and sticks. So the loop is always: drill the new thing just enough to be findable → immediately use it inside a real song against a recording.",
        drills: [
          "Drill the new element slowly, no time pressure.",
          "Turn on a recording and play along with it — the recording is your first bandmate and it won't wait.",
          "Say the numbers out loud as you play so number-thinking goes automatic.",
        ],
        doneWhen: "You've done one full pass of this loop (you'll do it for real in Unit 1.3).",
      },
    ],
  },
  {
    id: "1",
    title: "Phase 1 — First Contact (one song, one key)",
    installs: "One song, one key, playing along",
    note: "Key for this whole phase: C major (all white keys — the friendliest topography).",
    units: [
      {
        id: "1.1",
        title: "Unit 1.1 — The major triad recipe",
        goal: "Build any major chord on demand.",
        concept:
          "A major triad is one interval recipe: root, +4 half-steps, +3 half-steps. C → C, E, G. You're not memorizing 12 chords; you're memorizing one rule and 12 starting points. (On piano the felt hand-shape shifts between keys because of black/white topography — the recipe is constant, the feel becomes automatic through reps.)",
        drills: [
          "Build C, F, G as triads, right hand, middle of the keyboard.",
          "Confirm these three are instant.",
        ],
        doneWhen: "C, F, G major appear under your hand without counting.",
      },
      {
        id: "1.2",
        title: "Unit 1.2 — The two-hand split",
        goal: "Sound like real keys, not one-finger chords.",
        concept:
          "Split by register and role: left hand owns the low end (foundation), right hand owns the middle (color). The workhorse comping texture is left = root (single bass note), right = the triad up in the middle. That alone reads as real keyboard in these genres.",
        drills: [
          "For C: left hand plays a low C, right hand plays C–E–G in the middle. Repeat for F and G.",
        ],
        doneWhen:
          "You can play all three chords two-handed, root-in-left / triad-in-right, cleanly.",
      },
      {
        id: "1.3",
        title: "Unit 1.3 — Your first song, played along",
        goal: "Be jam-able on one song. This is the whole point of Phase 1.",
        concept:
          "“Let It Be” (key of C) is C – G – Am – F. You have C, G, F; Am is your first minor — see Unit 2.1, or just learn it as A–C–E for now.",
        drills: [
          "Get the four chords under your hands, slowly.",
          "Put on the recording and play along, left-root / right-triad, in time.",
          "Don't stop to fix mistakes — keep up with the track. In time beats perfect.",
        ],
        doneWhen:
          "You can hold the chord changes against the recording start to finish, even roughly.",
      },
    ],
  },
  {
    id: "2",
    title: "Phase 2 — The Key as a Place (the gravity)",
    installs: "Making C feel like home",
    note: "This phase fixes the main problem: chords feeling unanchored to their key.",
    units: [
      {
        id: "2.1",
        title: "Unit 2.1 — The minor triad (one transformation)",
        goal: "Build any minor chord with no new memorization.",
        concept:
          "A minor triad is the same major chord with the middle note dropped one half-step. C major (C–E–G) → C minor (C–E♭–G). One rule, every key. “Minor for all keys” isn't a second mountain — it's a single move applied to chords you already know.",
        drills: [
          "Take C, F, G major and drop each middle note → C minor, F minor, G minor.",
          "Now build the minors you'll actually use: Am (A–C–E), Em (E–G–B), Dm (D–F–A).",
        ],
        doneWhen: "You can convert any major you know into its minor instantly.",
      },
      {
        id: "2.2",
        title: "Unit 2.2 — Keys as gravity, not as chord-lists",
        goal: "Understand what “in a key” actually means.",
        concept:
          "A chord has no key by itself — C major is the I in C, the IV in G, the V in F. “Being in a key” means one chord is home (the tonic / I) and you feel every other chord relative to that home: how far it is, how much it wants to fall back. The skill you're missing isn't chord knowledge — it's a fixed reference point. Context is relational, so it can't be drilled on isolated chords; you have to practice the relationships.",
        doneWhen:
          "You can explain why the same C-major chord “means” something different in two different keys.",
      },
      {
        id: "2.3",
        title: "Unit 2.3 — The diatonic run (this is “chords in their scale”)",
        goal: "Turn the scattered chords of C into one family with one address.",
        concept:
          "Build a triad on every degree of the C scale and play them in order. The major/minor quality of each degree is locked by the scale's step-pattern and never changes from key to key: I, IV, V are major; ii, iii, vi are minor; vii is diminished. (C Dm Em F G Am B° → I ii iii IV V vi vii°.)",
        drills: [
          "Play the run up and back down, slowly, saying the numbers out loud (I, ii, iii, IV, V, vi).",
        ],
        doneWhen:
          "You can play the C diatonic run from memory and name each chord's number as it lands.",
        audio: { key: "C", degrees: ["I", "ii", "iii", "IV", "V", "vi", "vii°"], label: "Play the C diatonic run" },
      },
      {
        id: "2.4",
        title: "Unit 2.4 — The anchor drill (installs the gravity reflex)",
        goal: "Train your ear and hand to feel everything as a departure from / return to home.",
        drills: [
          "Play I, then another chord, then back to I: I–IV–I (C–F–C).",
          "I–V–I (C–G–C).",
          "I–vi–I (C–Am–C).",
          "Always return home. Feel each chord pulling back toward C.",
        ],
        doneWhen:
          "Leaving C and returning to it feels like leaving home and coming back — you can hear the resolution.",
        audio: { key: "C", degrees: ["I", "IV", "I", "V", "I", "vi", "I"], label: "Play the anchor drills in C" },
      },
      {
        id: "2.5",
        title: "Unit 2.5 — The cadence (planting the key)",
        goal: "Be able to announce a key unambiguously.",
        concept:
          "V→I is the strongest “this is the key” signal in Western music — the V pulls toward home harder than anything. In C that's G→C. IV–V–I (F–G–C) is the spine of a thousand songs.",
        drills: [
          "Play G→C repeatedly; feel the resolution.",
          "Play F–G–C as a loop.",
        ],
        doneWhen:
          "You can hear that F–G–C “lands” on home, and C is unmistakably the key.",
        audio: { key: "C", degrees: ["IV", "V", "I"], label: "Play the F–G–C cadence" },
      },
    ],
  },
  {
    id: "3",
    title: "Phase 3 — Portability (numbers + transposing)",
    installs: "Numbers + transposing to G, D",
    units: [
      {
        id: "3.1",
        title: "Unit 3.1 — Why the numbers map",
        goal: "Derive any key's chords instead of memorizing tables.",
        concept:
          "A major scale is a fixed step-pattern: W–W–H–W–W–W–H. Apply it from any note and you get that key's scale; number the notes 1–7. Stack thirds (“every other note”) on each degree and the chord qualities fall out automatically — and because the step-pattern is constant, the qualities are the same in every key (I/IV/V major, ii/iii/vi minor, vii° diminished). So a progression written in numbers is portable: pour any key into the same shape.",
        doneWhen:
          "Given a key, you can derive its I, IV, V, vi without looking anything up.",
      },
      {
        id: "3.2",
        title: "Unit 3.2 — Add G major",
        goal: "Make G feel like a place, the way C does.",
        drills: [
          "Diatonic run in G: G – Am – Bm – C – D – Em – F#° (say the numbers).",
          "Anchor drill in G: G–C–G, G–D–G, G–Em–G.",
          "Cadence: D→G, then C–D–G.",
        ],
        doneWhen: "G feels like a place, the way C does.",
        audio: { key: "G", degrees: ["I", "ii", "iii", "IV", "V", "vi", "vii°"], label: "Play the G diatonic run" },
      },
      {
        id: "3.3",
        title: "Unit 3.3 — Add D major",
        goal: "Make D feel like a place.",
        drills: [
          "Diatonic run in D: D – Em – F#m – G – A – Bm – C#°.",
          "Anchor + cadence in D (A→D, G–A–D).",
        ],
        doneWhen:
          "D feels like a place. You now own the three keys that cover most of your starter songs.",
        audio: { key: "D", degrees: ["I", "ii", "iii", "IV", "V", "vi", "vii°"], label: "Play the D diatonic run" },
      },
      {
        id: "3.4",
        title: "Unit 3.4 — Relabel and transpose",
        goal: "Make the number-thinking real and prove portability to yourself.",
        concept:
          "“With or Without You” (D: D–A–Bm–G = I–V–vi–IV) and “Let It Be” (C: C–G–Am–F = I–V–vi–IV) are the same song-object in two keys. Once you see that, moving a song to wherever a singer is comfortable is free.",
        drills: [
          "Take each starter song, find its key, and relabel its chords as numbers (reverse-engineer: “this chord is degree __”).",
          "Take one four-chord song you know in C and play it in G instead, by numbers.",
        ],
        doneWhen:
          "You can transpose a known song to a new key on the fly using numbers.",
      },
    ],
  },
  {
    id: "4",
    title: "Phase 4 — Comping Craft (the high-value skill)",
    installs: "The actual band-support skill",
    units: [
      {
        id: "4.1",
        title: "Unit 4.1 — The support mindset",
        goal: "Understand the job. In a band you are not carrying the song — you fill space and add color.",
        concept:
          "The most common beginner failure is playing too much. A keys player who plays less, in time, with good voicings, is more valuable than one playing busy fills. The craft is harmonic awareness plus restraint.",
        doneWhen: "You can state your job in a band in one sentence.",
      },
      {
        id: "4.2",
        title: "Unit 4.2 — Voicings and staying out of the way",
        goal: "Pick which notes of the chord, in what register, to avoid colliding with the singer and the bass.",
        concept:
          "Sit in the middle of the keyboard — out of the singer's range (high) and out of the bass's range (low).",
        drills: [
          "Play your starter songs' chords as middle-register right-hand voicings.",
          "Experiment with not always playing root–third–fifth in that order.",
        ],
        doneWhen: "Your chords sit in the middle and don't feel cluttered.",
      },
      {
        id: "4.3",
        title: "Unit 4.3 — Leaving space + the band wrinkle",
        goal: "Learn when to drop out and how to behave when other instruments cover the low end.",
        concept:
          "The full left-hand-bass texture matters most when it's just you or you-plus-one-guitar. In a full band with a bass player or a guitar holding the low end, a busy left hand muddies the bass's territory — comp middle-register chords in the right and keep the left sparse or silent. Both hands on the chord (pad-style) is a color you reach for, not a default you live in.",
        drills: [
          "Play a starter song two ways: (a) solo, left-hand bass + right-hand chords; (b) “band” mode, sparse/no left hand, right-hand chords only.",
          "Practice deliberately dropping out for a few bars and coming back.",
        ],
        doneWhen: "You can switch between solo texture and sparse band texture consciously.",
      },
      {
        id: "4.4",
        title: "Unit 4.4 — Groove",
        goal: "Add rhythmic feel. In time matters more than fancy.",
        drills: [
          "Play chord changes against a metronome or drum loop.",
          "Try simple rhythmic patterns (sustained pads vs. on-the-beat stabs).",
        ],
        doneWhen:
          "You can hold a steady groove through a full song without rushing or dragging.",
      },
    ],
  },
  {
    id: "5",
    title: "Phase 5 — Ear Training (runs in PARALLEL, from early)",
    installs: "Hearing the key and the changes",
    note: "Don't wait until Phase 5 to start this — fold it in from Phase 2 onward. The ear is the rate-limiter for “play with anyone.” Use the ear-training panel below.",
    units: [
      {
        id: "5.1",
        title: "Unit 5.1 — Hear the tonic",
        drills: ["Play a cadence (F–G–C), then hum/find the home note. Hear where “home” is."],
        doneWhen: "You can find the tonic of a song by ear most of the time.",
      },
      {
        id: "5.2",
        title: "Unit 5.2 — Hear the four core chords go by",
        drills: [
          "Play I–V–vi–IV loops and learn the sound of each move.",
          "Start predicting the next chord before it arrives.",
        ],
        doneWhen: "You can usually tell which of I/IV/V/vi is sounding.",
      },
      {
        id: "5.3",
        title: "Unit 5.3 — Guess a new song",
        concept: "Guessing right ~70% of the time is enough to hang.",
        drills: [
          "Pull up a new four-chord song you don't know the chords to. Guess the progression by ear, then check.",
        ],
        doneWhen:
          "You can guess a simple song's progression and be right more often than not.",
      },
    ],
  },
  {
    id: "6",
    title: "Phase 6 — Playing With a Human",
    installs: "The shared protocol, taking direction",
    units: [
      {
        id: "6.1",
        title: "Unit 6.1 — The protocol",
        goal: "Understand how strangers play together with no rehearsal.",
        concept:
          "Someone says “it's in G, four-chord, I–V–vi–IV” — and because you think in numbers and own G, you're in. The chart/numbers are the shared API.",
        doneWhen:
          "Given a key and a number progression you've never heard as a song, you can start playing it.",
      },
      {
        id: "6.2",
        title: "Unit 6.2 — Sitting in",
        drills: [
          "Play along with recordings as your “patient bandmate” until it's locked.",
          "Then play with a friend with a guitar — easy mode, because you already ran the protocol against recordings.",
          "Practice taking direction: key changes, “drop out here,” “just pads.”",
        ],
        doneWhen:
          "You've played a full song with another live human, in a support role, start to finish.",
      },
    ],
  },
  {
    id: "7",
    title: "Phase 7 — Melody / Improv (OPTIONAL, advanced)",
    installs: "Soloing over changes",
    note: "Genuinely optional in a support role. Understand where it's headed; don't let it pull focus from comping.",
    units: [
      {
        id: "7.1",
        title: "Unit 7.1 — Scale = safe, chord tones = strong",
        concept:
          "This is the gravity idea from Phase 2, fractal'd down one level. The scale (the key's 7 notes) is your safe sandbox — stay inside it and nothing sounds wrong. The chord tones (the 3 notes of the chord sounding right now) are the strong notes — the resting points that sound locked-in; land on one on a strong beat and it sounds deliberate. The other scale notes are connective tissue — great in motion, but they pull toward a chord tone if you sit on them.",
      },
      {
        id: "7.2",
        title: "Unit 7.2 — Moving targets",
        concept:
          "The scale stays the same but which notes are “strong” shifts as the chord changes underneath. In C the scale is always C–D–E–F–G–A–B; but when the chord is C, C–E–G are the gravity wells, and when it moves to F, now F–A–C are strong and E becomes a tension note wanting to fall to F. Same scale, moving targets. Feeling both levels — progression pulling toward the tonic, melody pulling toward the current chord's tones — is “playing the changes.”",
        drills: [
          "Over a I–IV–V loop in C, improvise staying only in the scale.",
          "Then deliberately target the current chord's tones on strong beats; let the rest be passing notes.",
        ],
        doneWhen:
          "You can solo over a simple progression and land on chord tones intentionally.",
      },
    ],
  },
];

// Every checkable id in the plan, used to compute overall progress.
export const ALL_CHECK_IDS = PHASES.flatMap((p) =>
  p.units.flatMap((u) => [
    ...(u.drills || []).map((_, i) => `${u.id}-drill-${i}`),
    ...(u.doneWhen ? [`${u.id}-done`] : []),
  ])
);

// Checkable ids for one phase (for per-phase progress counts).
export const phaseCheckIds = (phase) =>
  phase.units.flatMap((u) => [
    ...(u.drills || []).map((_, i) => `${u.id}-drill-${i}`),
    ...(u.doneWhen ? [`${u.id}-done`] : []),
  ]);
